import React, { memo } from "react";
import { ChakraStylesConfig, Select } from "chakra-react-select";
import { InputActionMeta, Props as SelectProps } from "react-select";
import { Box, StylesProvider, useMultiStyleConfig, useTheme } from "@chakra-ui/react";

export declare type Size = "sm" | "md" | "lg";
export declare type TagVariant = "subtle" | "solid" | "outline" | undefined;
export declare type SelectedOptionStyle = "color" | "check";
export type SizeProps = {
  sm: string | number;
  md: string | number;
  lg: string | number;
};

export interface UiDropdownProps extends SelectProps {
  size?: Size;
  minW?: string;
  colorScheme?: string;
  isInvalid?: boolean;
  tagVariant?: TagVariant;
  hasStickyGroupHeaders?: boolean;
  selectedOptionStyle?: SelectedOptionStyle;
  selectedOptionColor?: string;
  multiple?: boolean;
  onInputChange?: (newValue: string, actionMeta: InputActionMeta) => void;
  menuWidth?: string;
}

function Menu({ children, w, innerProps, selectProps: { size } }: any) {
  const menuStyles = useMultiStyleConfig("Menu", {});
  const chakraTheme = useTheme();
  const borderRadii: SizeProps = {
    sm: chakraTheme.radii.sm,
    md: chakraTheme.radii.md,
    lg: chakraTheme.radii.md,
  };

  return (
    <Box
      sx={{
        position: "absolute",
        top: "100%",
        my: "8px",
        w,
        zIndex: 1,
        overflowY: "auto",
        overflowX: "hidden",
        rounded: borderRadii[size as Size],
      }}
      {...innerProps}
    >
      <StylesProvider value={menuStyles}>{children}</StylesProvider>
    </Box>
  );
}

export const UiDropdown = memo(
  React.forwardRef(
    (
      {
        multiple = false,
        menuWidth = "100%",
        components = {},
        size = "sm",
        minW,
        onInputChange,
        chakraStyles,
        ...rest
      }: UiDropdownProps,
      ref
    ) => {
      const localChakraStyles: ChakraStylesConfig = {
        control: (provided: any, state: any) => ({
          ...provided,
          bg: "white",
          minH: "2.5rem",
          fontSize: "14px",
          color: "neutral.gray500",
          minWidth: minW || "8em",
          fontWeight: "400",
          textTransform: "none",
          _hover: { cursor: "pointer" },
          ...(chakraStyles?.control?.(provided, state) || {}),
        }),
        option: (provided: any, state: any) => ({
          ...provided,
          bg: state.isFocused ? "#ECEFF6" : undefined,
          fontSize: "14px",
          color: "neutral.gray500",
          _selected: {
            color: "blue",
          },
          ...(chakraStyles?.option?.(provided, state) || {}),
        }),
        menuList: (provided: any, state: any) => ({
          ...provided,
          fontWeight: "400",
          textTransform: "none",
          overflowX: "hidden",
          fontSize: "14px",
          color: "neutral.gray500",
          ...(chakraStyles?.menuList?.(provided, state) || {}),
        }),
        placeholder: (provided: any, state: any) => ({
          ...provided,
          color: "neutral.gray300",
          ...(chakraStyles?.placeholder?.(provided, state) || {}),
        }),
      };
      return (
        <Select
          // @ts-ignore
          ref={ref}
          size={size}
          isMulti={multiple}
          minMenuHeight={26}
          maxMenuHeight={200}
          useBasicStyles
          // menuIsOpen // Use this one to debug
          // components={{
          //   // eslint-disable-next-line react/no-unstable-nested-components
          //   Menu: (props: any) => <Menu w={menuWidth} {...props} />,
          //   ...components,
          // }}
          chakraStyles={localChakraStyles}
          onInputChange={onInputChange}
          {...rest}
        />
      );
    }
  )
);
